// Client for the RingbaX admin chat endpoint: AdminCopilotGatewayService at
// /api/v1/admin-copilot/invocations on the internal base URL, like every other
// backend API. The signed-in admin's bearer is the only credential; there is
// no account linking — the copilot asks for account ids in conversation.
//
// Body: { prompt, threadHistory? } -> text/event-stream
//
// Auth: the caller passes a Cognito access token; we send it as
// `Authorization: Bearer <jwt>` and retry once after `refreshAccessToken`
// (stale tokens can come back as 401 or 403 on this path). This module never
// imports Vuex — the store wires the token + refresh callback in. Keeps the
// network layer unit-testable in isolation.
//
// The backend emits these SSE events, which we translate to the panel's
// internal shape on the fly:
//
//   { type: "text_chunk",  text, phase: "plan" | "answer" }
//   { type: "tool_start",  id, name, args }
//   { type: "tool_result", id, result }
//   { type: "citations",   items: [{ label, to }] }
//   { type: "done" }
//   { type: "error",       message }

// Exported so a failure report can name the exact endpoint that was called.
export const DEFAULT_COPILOT_URL = process.env.VUE_APP_INTERNAL_SERVICE_BASE_URL
  ? new URL('/api/v1/admin-copilot/invocations', process.env.VUE_APP_INTERNAL_SERVICE_BASE_URL).href
  : ''

export async function* parseSseStream(response, signal) {
  if (!response || !response.body) {
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      if (signal && signal.aborted) {
        return
      }

      const { value, done } = await reader.read()
      if (done) {
        break
      }

      buffer += decoder.decode(value, { stream: true })

      let nlIdx
      while ((nlIdx = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, nlIdx)
        buffer = buffer.slice(nlIdx + 2)

        const lines = frame.split('\n')
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const payload = line.slice(6)
            try {
              yield JSON.parse(payload)
            } catch (e) {
              // skip malformed frames
            }
          }
        }
      }
    }
  } finally {
    try {
      // Close the underlying HTTP stream on early exit; no-op if already done.
      await reader.cancel()
    } catch (e) { /* ignore */ }

    try {
      reader.releaseLock()
    } catch (e) { /* ignore */ }
  }
}

function postChat({ copilotUrl, accessToken, prompt, threadHistory, conversationId, signal }) {
  // The gateway contract binds thread turns as { role, text }; the store still
  // builds { role, content } turns, so map here at the boundary.
  const history = (threadHistory || []).map(t => ({ role: t.role, text: t.content ?? t.text ?? '' }))

  return fetch(copilotUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      // The gateway keys the backend chat session off this; one value per
      // conversation keeps all turns on the same session.
      'X-Conversation-Id': conversationId || ''
    },
    body: JSON.stringify({
      prompt,
      threadHistory: history
    }),
    signal
  })
}

function translateEvent(event) {
  switch (event.type) {
    case 'text_chunk':
      // `segment` groups chunks belonging to one model turn. The store starts a new
      // block when it changes, so two turns never get concatenated into one line.
      return event.phase === 'plan'
        ? { type: 'plan_chunk', text: event.text }
        : { type: 'answer_chunk', text: event.text, segment: event.segment }
    case 'tool_start':
    case 'tool_result':
    case 'citations':
    case 'error':
    case 'done':
      return event
    default:
      return null
  }
}

export async function runPrompt(prompt, options = {}) {
  const {
    onEvent,
    signal,
    threadHistory,
    conversationId,
    accessToken,
    refreshAccessToken,
    copilotUrl = DEFAULT_COPILOT_URL
  } = options

  const emit = onEvent || (() => {})

  if (!copilotUrl) {
    emit({ type: 'error', message: 'Copilot endpoint is not configured.' })
    emit({ type: 'done' })
    return
  }

  if (!accessToken) {
    emit({ type: 'error', message: 'Not signed in. Reload and log back in.' })
    emit({ type: 'done' })
    return
  }

  // Stale tokens can surface as 401 or 403 on this path, so both trigger
  // the single refresh-and-retry.
  const isAuthFailure = (status) => status === 401 || status === 403

  let response
  try {
    response = await postChat({ copilotUrl, accessToken, prompt, threadHistory, conversationId, signal })

    // Token may have expired. Refresh once and retry.
    if (isAuthFailure(response.status) && typeof refreshAccessToken === 'function') {
      let fresh
      try {
        fresh = await refreshAccessToken()
      } catch (e) {
        emit({ type: 'error', message: 'Session expired. Reload and log back in.' })
        emit({ type: 'done' })
        return
      }

      if (!fresh) {
        emit({ type: 'error', message: 'Session expired. Reload and log back in.' })
        emit({ type: 'done' })
        return
      }

      response = await postChat({ copilotUrl, accessToken: fresh, prompt, threadHistory, conversationId, signal })
    }
  } catch (err) {
    if (err.name === 'AbortError') {
      emit({ type: 'done' })
      return
    }

    emit({ type: 'error', message: `Network error: ${err.message}` })
    emit({ type: 'done' })
    return
  }

  if (!response.ok) {
    let detail = ''
    try {
      const body = await response.json()
      detail = body.error || JSON.stringify(body)
    } catch (e) {
      detail = response.statusText
    }

    emit({ type: 'error', message: `Copilot returned ${response.status}: ${detail}` })
    emit({ type: 'done' })
    return
  }

  if (!response.body) {
    emit({ type: 'error', message: 'Copilot returned no stream body.' })
    emit({ type: 'done' })
    return
  }

  try {
    for await (const event of parseSseStream(response, signal)) {
      const translated = translateEvent(event)
      if (!translated) {
        continue
      }

      emit(translated)
      if (translated.type === 'done') {
        return
      }
    }

    emit({ type: 'done' })
  } catch (err) {
    if (err.name === 'AbortError') {
      emit({ type: 'done' })
      return
    }

    emit({ type: 'error', message: `Stream error: ${err.message}` })
    emit({ type: 'done' })
  }
}
